import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  Destroyable,
  SaveForm,
  selectRentalById,
  untilDestroy,
  selectSubdomain,
  Rental,
  notAllSpacesValidator,
  notAllNumbersValidator,
  RentalNameNotTakenValidator,
  CreateRentalPayload,
  addSubUnitRentals,
  UpdateSubunitRentals,
  UpdateSubunitRentalPayload,
  isSavingRental,
  integer,
  createRentalPayloadFromMainUnit,
  selectRentalSubUnits,
} from '@tokeet-frontend/tv3-platform'
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { map, take } from 'rxjs/operators'
import { of } from 'rxjs'

@Component({
  selector: 'app-subunit-settings-dialog',
  templateUrl: './subunit-settings-dialog.component.html',
  styleUrls: ['./subunit-settings-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class SubunitSettingsDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(140),
        notAllNumbersValidator,
        notAllSpacesValidator,
      ],
    ],
    multi_unit_status: [1],
    multi_unit_priority: [null, [integer(), Validators.min(1), Validators.max(100)]],
  })

  loading$ = this.store.pipe(select(isSavingRental))

  subDomain: string
  mainRental: Rental
  currentRental: Rental

  constructor(
    public dialogRef: MatDialogRef<SubunitSettingsDialogComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: { rentalId?: string; mainRentalId: string }
  ) {
    super()
  }

  ngOnInit(): void {
    if (this.data.rentalId) {
      this.form
        .get('name')
        .addAsyncValidators(RentalNameNotTakenValidator.createValidator(this.store, this.data.rentalId))
      this.store.pipe(select(selectRentalById(this.data.rentalId)), untilDestroy(this)).subscribe((rental) => {
        this.currentRental = rental
        this.form.patchValue({
          name: rental.name,
          multi_unit_status: rental?.multi_unit_status === 0 ? 0 : 1,
          multi_unit_priority: rental.multi_unit_priority,
        })
      })
    }

    this.store.pipe(select(selectRentalById(this.data.mainRentalId)), untilDestroy(this)).subscribe((rental) => {
      this.mainRental = rental
    })

    const existingPriorities$ = this.store.pipe(
      select(selectRentalSubUnits(this.data.mainRentalId)),
      map((units) => units.filter((u) => !!u.multi_unit_priority).map((unit) => +unit.multi_unit_priority))
    )

    this.store.pipe(select(selectSubdomain), untilDestroy(this)).subscribe((subDomain) => {
      this.subDomain = subDomain
    })

    this.form.get('multi_unit_priority').addAsyncValidators((ctrl: AbstractControl) => {
      if (!ctrl.value) return of(null)
      return existingPriorities$.pipe(
        take(1),
        map((priorities) => {
          priorities = priorities.filter((p) => p != this.currentRental?.multi_unit_priority)
          if (priorities.includes(ctrl.value)) {
            return { existing: true }
          } else {
            return null
          }
        })
      )
    })
  }

  close() {
    this.dialogRef.close()
  }

  getNewRentalPayload(data: UpdateSubunitRentalPayload): CreateRentalPayload {
    return {
      ...data,
      ...createRentalPayloadFromMainUnit(this.mainRental, data.name, this.subDomain),
    }
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const data = this.form.getRawValue()

    if (!this.data.rentalId) {
      this.store.dispatch(addSubUnitRentals({ items: [this.getNewRentalPayload(data)] }))
    } else {
      this.store.dispatch(UpdateSubunitRentals({ items: [{ id: this.data.rentalId, payload: data }] }))
    }

    this.close()
  }
}
